import Cookies from "js-cookie";
import { jwtDecode } from "jwt-decode";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../config/axiosConfig";

const ACTIONS = {
  AUTH_START: "auth_start",
  AUTH_SUCCESS: "auth_success",
  AUTH_ERROR: "auth_error",
  AUTH_LOGOUT: "auth_logout",
  REFRESH_TOKEN: "refresh_token",
  SET_LOADING: "set_loading",
};

const initialState = {
  loading: true,
  error: null,
  isAuthenticated: false,
  user: null,
  userId: null,
  accessToken: null,
  refreshToken: null,
  role: null,
  isAdmin: false,
};

const authReducer = (state, action) => {
  switch (action.type) {
    case ACTIONS.AUTH_START:
      return { ...state, loading: true, error: null };
    case ACTIONS.AUTH_SUCCESS:
      return {
        ...state,
        loading: false,
        isAuthenticated: true,
        user: action.payload.user,
        userId: action.payload.user?._id || null,
        accessToken: action.payload.accessToken,
        refreshToken: action.payload.refreshToken,
        role: action.payload.user?.role || action.payload.role, // Prioriser user.role
        error: null,
        isAdmin: false,
      };
    case ACTIONS.AUTH_ERROR:
      return { ...state, loading: false, error: action.payload };
    case ACTIONS.AUTH_LOGOUT:
      Cookies.remove("access_token");
      Cookies.remove("refresh_token");
      return { ...initialState, loading: false };
    case ACTIONS.REFRESH_TOKEN:
      return {
        ...state,
        accessToken: action.payload.accessToken,
        refreshToken: action.payload.refreshToken,
      };
    case ACTIONS.SET_LOADING:
      return { ...state, loading: action.payload };
    default:
      return state;
  }
};

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const navigate = useNavigate();
  const isRefreshing = useRef(false);
  const hasCheckedAuth = useRef(false);
  const retryCount = useRef(0);
  const maxRetries = 3;
  const rateLimitCooldown = useRef(false);

  const setProfile = useCallback(
    (updatedUser) => {
      dispatch({
        type: ACTIONS.AUTH_SUCCESS,
        payload: {
          accessToken: state.accessToken,
          refreshToken: state.refreshToken,
          user: updatedUser,
          role: updatedUser?.role || state.role,
        },
      });
    },
    [state.accessToken, state.refreshToken, state.role]
  );

  const logout = useCallback(async () => {
    try {
      // Laisser le backend supprimer les cookies HttpOnly
      await api.post("/auth/logout");
    } catch (e) {
      // On continue même si ça échoue
    } finally {
      dispatch({ type: ACTIONS.AUTH_LOGOUT });
      navigate("/home-page", { replace: true });
      toast.info("Déconnexion réussie.");
    }
  }, [navigate]);

  const setAuthData = useCallback(
    (accessToken, refreshToken, user) => {
      try {
        const decodedToken = jwtDecode(accessToken);
        Cookies.set("access_token", accessToken, {
          secure: process.env.NODE_ENV === "production",
          sameSite: process.env.NODE_ENV === "production" ? "None" : "Lax",
          expires: 12 / 24,
        });
        Cookies.set("refresh_token", refreshToken, {
          secure: process.env.NODE_ENV === "production",
          sameSite: process.env.NODE_ENV === "production" ? "None" : "Lax",
          expires: 7,
        });
        dispatch({
          type: ACTIONS.AUTH_SUCCESS,
          payload: {
            accessToken,
            refreshToken,
            user,
            role: user?.role || decodedToken.role,
          },
        });
      } catch (error) {
        console.error("Erreur lors du décodage du token:", error);
        dispatch({ type: ACTIONS.AUTH_ERROR, payload: "Token invalide" });
        logout();
      }
    },
    [logout]
  );

  const refreshAuthToken = useCallback(async () => {
    if (isRefreshing.current || rateLimitCooldown.current) {
      console.log(
        "Rafraîchissement en cours ou en cooldown (429 détecté), abandon."
      );
      return Promise.resolve();
    }
    isRefreshing.current = true;

    const storedRefreshToken = Cookies.get("refresh_token");
    console.log(
      "Tentative de rafraîchissement - refreshToken:",
      storedRefreshToken
    );

    if (!storedRefreshToken) {
      console.error("Le refresh_token est manquant");
      dispatch({ type: ACTIONS.AUTH_LOGOUT });
      return Promise.reject(new Error("Refresh token manquant"));
    }

    try {
      const response = await api.post("/auth/refresh-token", {
        refresh_token: storedRefreshToken,
      });
      const { access_token, refresh_token, user } = response.data;
      console.log("Rafraîchissement réussi - nouveaux tokens:", {
        access_token,
        refresh_token,
      });
      setAuthData(access_token, refresh_token, user);
      retryCount.current = 0;
      return Promise.resolve();
    } catch (error) {
      console.error(
        "Erreur lors du rafraîchissement du token:",
        error.response?.data || error.message
      );
      if (error.response?.status === 429) {
        console.log(
          "Erreur 429 détectée lors du rafraîchissement, mise en cooldown."
        );
        rateLimitCooldown.current = true;
        setTimeout(() => {
          rateLimitCooldown.current = false;
          console.log(
            "Cooldown terminé, nouvelle tentative de rafraîchissement possible."
          );
        }, 60000);
      }
      dispatch({
        type: ACTIONS.AUTH_ERROR,
        payload: "Échec du rafraîchissement du token",
      });
      logout();
      return Promise.reject(error);
    } finally {
      isRefreshing.current = false;
    }
  }, [logout, setAuthData]);

  const fetchUser = useCallback(async (accessToken) => {
    try {
      const response = await api.get("/users/me");

      if (!response.data || typeof response.data !== "object") {
        console.error("Réponse de /users/me invalide:", response.data);
        throw new Error(
          `Réponse invalide de /users/me: ${JSON.stringify(response.data)}`
        );
      }

      const requiredFields = ["_id", "email", "nom"];
      const missingFields = requiredFields.filter(
        (field) => !(field in response.data)
      );
      if (missingFields.length > 0) {
        console.error("Champs utilisateur manquants:", missingFields);
        throw new Error(
          `Champs utilisateur manquants: ${missingFields.join(
            ", "
          )}. Réponse: ${JSON.stringify(response.data)}`
        );
      }

      console.log("Utilisateur récupéré avec succès:", response.data);
      return response.data;
    } catch (error) {
      if (error.response) {
        const { status, data } = error.response;
        console.error(
          `Erreur ${status} lors de la récupération de l’utilisateur:`,
          data
        );
        if (status === 429) {
          console.log(
            "Erreur 429 détectée lors de fetchUser, mise en cooldown."
          );
          rateLimitCooldown.current = true;
          setTimeout(() => {
            rateLimitCooldown.current = false;
            console.log(
              "Cooldown terminé, nouvelle tentative de fetchUser possible."
            );
          }, 60000);
        }
        throw error;
      } else if (error.request) {
        console.error("Aucune réponse du serveur:", error.request);
        throw new Error(
          "Aucune réponse du serveur. Vérifiez votre connexion réseau."
        );
      } else {
        console.error(
          "Erreur lors de la configuration de la requête:",
          error.message
        );
        throw new Error(
          `Erreur lors de la configuration de la requête: ${error.message}`
        );
      }
    }
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const userData = await fetchUser();
      if (userData) {
        dispatch({ type: ACTIONS.AUTH_SUCCESS, payload: { user: userData, role: userData?.role } });
        return userData;
      }
    } catch (error) {
      console.error("refreshUser failed:", error);
    }
    return null;
  }, [fetchUser, dispatch]);

  const checkAuth = useCallback(async () => {
    const publicRoutes = [
      '/auth/login',
      '/auth/register',
      '/register-success',
      '/welcome/particulier',
      '/subscribe',
      '/verify-email',
      '/forgot-password'
    ];

    const currentPath = window.location.pathname;

    // Skip complet sur les routes publiques
    if (publicRoutes.some(route => currentPath.startsWith(route))) {
      dispatch({ type: ACTIONS.SET_LOADING, payload: false });
      hasCheckedAuth.current = true;
      return;
    }

    if (retryCount.current >= maxRetries || rateLimitCooldown.current) {
      return;
    }

    dispatch({ type: ACTIONS.AUTH_START });

    try {
      const user = await fetchUser();
      dispatch({
        type: ACTIONS.AUTH_SUCCESS,
        payload: { 
          user, 
          role: user?.role 
        },
      });
      console.log("[checkAuth] Authentification confirmée via /users/me");
    } catch (error) {
      console.error("[checkAuth] Échec vérification :", error?.response?.status);
      dispatch({ type: ACTIONS.AUTH_LOGOUT });
    } finally {
      hasCheckedAuth.current = true;
    }
  }, [fetchUser]);

  useEffect(() => {
    if (hasCheckedAuth.current) return;
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (rateLimitCooldown.current) {
        console.log(
          "Intervalle de rafraîchissement désactivé en raison d’une erreur 429."
        );
        return;
      }
      const accessToken = Cookies.get("access_token");
      if (accessToken) {
        const decoded = jwtDecode(accessToken);
        if (decoded.exp * 10000 - Date.now() < 300000) {
          console.log(
            "Token proche de l’expiration, rafraîchissement déclenché"
          );
          refreshAuthToken().catch(() =>
            console.log("Rafraîchissement échoué")
          );
        }
      }
    }, 300000);

    return () => clearInterval(interval);
  }, [refreshAuthToken]);

  const register = useCallback(
    async (userData) => {
      dispatch({ type: ACTIONS.AUTH_START });

      

      try {
        const response = await api.post("/auth/register", userData);

        if (response.status === 201) {
          toast.success("Utilisateur enregistré avec succès");
          return response.data;
        } else {
          throw new Error("L’inscription a échoué");
        }
      } catch (err) {
        dispatch({
          type: ACTIONS.AUTH_ERROR,
          payload: err.response?.data?.message || "Erreur lors de l’inscription",
        });
        toast.error(err.response?.data?.message || "Erreur lors de l’inscription");
        throw err;
      }
    },
    []
  );

  const login = useCallback(
    async (email, password, fetchUserProgression) => {
      dispatch({ type: ACTIONS.AUTH_START });

      try {
        const response = await api.post("/auth/login", { email, password });
        const { access_token, refresh_token, user } = response.data;

        if (access_token && refresh_token) {
          setAuthData(access_token, refresh_token, user);

          if (fetchUserProgression) {
            try {
              await fetchUserProgression();
            } catch (error) {
              console.error("Erreur progression après login:", error);
              toast.error("Erreur chargement progression.");
            }
          }
        } else {
          throw new Error("Tokens manquants");
        }
      } catch (err) {
        dispatch({
          type: ACTIONS.AUTH_ERROR,
          payload: err.response?.data?.message || "Erreur connexion",
        });
        toast.error("Erreur lors de la connexion");
        throw err;
      }
    },
    [setAuthData, dispatch]
  );

  const ProtectedRoute = ({ children }) => {
    if (state.loading) {
      return (
        <div className="flex justify-center items-center min-h-screen">
          Chargement...
        </div>
      );
    }
    if (state.error) {
      return (
        <div className="flex justify-center items-center min-h-screen">
          <div className="text-center">
            <p className="text-red-500 mb-4">{state.error}</p>
            <button
              onClick={() => checkAuth()}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              Réessayer
            </button>
          </div>
        </div>
      );
    }
    if (!state.isAuthenticated) {
      return (
        <Navigate
          to="/auth/login"
          state={{ from: window.location.pathname }}
          replace
        />
      );
    }
    return children;
  };

  const contextValue = useMemo(
    () => ({
      register,
      login,
      logout,
      isAuthenticated: state.isAuthenticated,
      loading: state.loading,
      error: state.error,
      user: state.user,
      userId: state.userId,
      accessToken: state.accessToken,
      refreshToken: state.refreshToken,
      refreshAuthToken,
      fetchUser,
      refreshUser,
      ProtectedRoute,
      setProfile,
      isPremium: state.user?.isPremium ?? false,
      isAdmin: state.isAdmin,
      role: state.role,
    }),
    [register, login, logout, state, refreshAuthToken, fetchUser]
  );

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};
